import type { BreathSegment, RangeInfo, Vibrato } from '@muzly/voice-engine';

import { algoVersionOf } from './analyContract';

import type {
  AnalyAxisKey,
  AnalyBreathSegment,
  AnalyCapture,
  AnalyGraph,
  AnalyHighMetrics,
  AnalyLowMetrics,
  AnalyScore,
  AnalySessionResult,
  AnalySongMetrics,
  AnalySongPartScore,
  AnalySongScore,
  AnalySongTiming,
  AnalyStereo,
  AnalySubmitBody,
  AnalySustainMetrics,
  SustainSubmitBody,
} from './analyContract';
import type { CaptureSource } from '../capture';
import type { SongScore, SongSegmentScore } from '../songScoring';

/**
 * toAnalySubmitBody — 앱이 잰 값 → 서버가 받는 테이크 body.
 *
 * **필드를 테이크에 귀속시키는 것이 이 함수의 일 전부다.** 서버는 `AnalyFieldPart`
 * (SPEC §2.1:214-236)로 잘못 실린 필드를 `400 INVALID`로 거절한다 — `sustainSec`을 `low`
 * 제출에 실으면 그 회차가 통째로 실패한다. 귀속표를 코드 한 곳에 두어 호출부마다
 * 판단이 갈리지 않게 한다.
 *
 * **없는 값은 키를 뺀다.** `0`으로 채우면 서버에 "그때는 맑기 0이었다"가 저장되고,
 * `undefined`를 담으면 `JSON.stringify`가 키를 지우긴 하나 그건 우연이지 계약이 아니다
 * (`apps/web/CONTEXT.md` "Measured vs not measured").
 *
 * **입력이 부분적인 것은 정상이다.** 제출은 두 시점에서 일어난다: 테이크를 마친 순간
 * (`useRecordMachine`의 `advance`)과 진단을 마친 순간(`finish`). 앞 시점에는 아직 못 잰
 * 값이 있다 — `range`는 저음·고음 클립이 **둘 다** 있어야 나오므로(`voice-engine`의
 * `buildReport`) 저음 테이크 직후에는 그 테이크 단독 끝음(`lowHz`·`lowNote`)만 있다. 같은 함수가 두 시점을
 * 모두 처리하고, 나중 제출이 같은 `stereo`를 덮어쓴다(서버 D18·C5 — 복합키 멱등).
 */

/** 회차에서 잰 값들. 아직 안 잰 것은 없는 채로 들어온다. */
export interface AnalyMetricsInput {
  sustainSec?: number;
  hnrDb?: number;
  resonanceRatio?: number;
  resonanceLevelDb?: number;
  resonanceCenterHz?: number;
  brightnessHz?: number;
  bandwidthHz?: number;
  vibrato?: Vibrato;
  breath?: BreathSegment[];
  /** 저음·고음이 다 유효할 때만. 그전에는 `null`이거나 없다. */
  range?: RangeInfo | null;
  /**
   * 그 테이크 하나에서 잰 음역 끝. `range`가 아직 없는 테이크 경계 제출용이다 — 회차가 저음
   * 뒤에 끊기면 서버에 남는 저음 끝이 이것뿐이다. `range`가 있으면 그쪽이 이긴다(시도 합침).
   */
  lowHz?: number;
  lowNote?: string;
  highHz?: number;
  highNote?: string;
  songScore?: SongScore;
}

/**
 * 서버로 나가는 호흡 구간 — **점수 있는 것만**. 서버 계약(`AnalyBreathSegment`)은 `id`와
 * `score` 만 나르므로 점수 없는 줄은 건너간 뒤 빈 껍데기가 된다(복원하면 `toBreath` 가
 * 어차피 버린다). 구간의 초는 이 기기의 레코드에만 남는다.
 */
const breathForServer = (
  segments: BreathSegment[] | undefined,
): AnalyBreathSegment[] | undefined => {
  const scored = (segments ?? []).flatMap((s) =>
    s.score === undefined ? [] : [{ id: s.id, score: s.score }],
  );
  return scored.length > 0 ? scored : undefined;
};

export interface AnalyTakeInput {
  metrics: AnalyMetricsInput;
  /** 이번 테이크 점수 0~100. */
  score?: number;
  mediaId?: string;
  /**
   * 회차 산출물. **마지막으로 나가는 제출에만** 채운다 — 이게 실린 제출을 받은 서버가
   * 집계하고 `done`으로 전이한다(D20).
   */
  session?: AnalySessionResult;
  /**
   * 지속음의 소리 안정성 곡선. `sustain` 제출에만 실린다 — low/high/song에 채워 넣어도
   * 아래 매퍼가 뺀다. 서버는 stereo와 무관하게 `graph$`를 그대로 저장하니(거절 없음,
   * `muzly-backend-api/src/modules/vocal/transformer.ts:268` `submitToModel`이 stereo
   * 대조 없이 `graph$`를 복사하고, `proxy.ts:127-139`의 STEP.3 검증도 stereo와 대조하지
   * 않는다), sustain 전용은 서버 귀속표가 아니라 앱 쪽 결정이다(plan Decisions).
   */
  graph?: AnalyGraph;
  /**
   * 이번 회차를 만든 캡처 경로. `algoVersion` 계산의 재료다. 없으면 `'native'`로 본다 —
   * 슬라이스 03에서 `useRecordMachine.ts`가 `source`를 배선하기 전까지의 임시 기본값이다.
   */
  source?: CaptureSource;
  /**
   * 이번 테이크의 녹음 조건. 네이티브 녹음이 있을 때만 들어온다 — 브라우저·mock 경로는
   * 아예 없다(`toAnalyCapture`가 `undefined`를 돌려주고 `defined()`가 키를 지운다).
   */
  capture?: AnalyCapture;
}

/**
 * 서버가 받는 축(SPEC §2.1 `AnalyAxisKey` LUT). 앱의 `STORED_METRIC_KEYS`와 **값이 같지만**
 * 타입은 다르다 — 그쪽은 엔진의 `MetricKey`(7종)에서 고른 4종이라, 축이 하나 늘면
 * `DiagnosisRecord.scores`에 서버가 모르는 키가 실릴 수 있다. 그때 서버는 그 제출을 통째로
 * `400 INVALID`로 거절한다(§4) — 진단을 마친 사용자에게서만 나는 실패다.
 *
 * 그래서 캐스트가 아니라 **거르기**다. 모르는 축은 그 축만 빠지고 나머지는 저장된다.
 */
const ANALY_AXIS_KEYS: AnalyAxisKey[] = [
  'pitchAccuracy',
  'pitchStability',
  'sustain',
  'vibrato',
];

const isAnalyScore = (score: {
  key: string;
  val: number;
}): score is AnalyScore => (ANALY_AXIS_KEYS as string[]).includes(score.key);

/** 회차 축 점수 → 서버가 아는 축만. 형태(`{key,val}[]`)는 양쪽이 같아 변환하지 않는다. */
export const toAnalyScores = (
  scores: { key: string; val: number }[],
): AnalyScore[] => scores.filter(isAnalyScore);

/** 값이 있는 키만 남긴다. `null`은 "못 쟀다"라 없는 것과 같이 다룬다. */
export const defined = <T extends object>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (val !== undefined && val !== null) {
      out[key as keyof T] = val as T[keyof T];
    }
  }
  return out;
};

/**
 * 테이크와 무관하게 실리는 것 — 테이크 점수·미디어·회차 산출물.
 *
 * 반환 타입을 `Record<string, unknown>`으로 두면 안 된다. 그걸 body 리터럴에 스프레드하는
 * 순간 필드명 검사가 사라져서, 틀린 이름이 빌드를 그대로 통과한다(실측: bogus 필드가 green).
 * 계약 필드에서 뽑은 타입을 붙여야 오타가 컴파일에서 죽는다.
 */
type AnalyCommonFields = Pick<
  SustainSubmitBody,
  | 'score'
  | 'mediaId'
  | 'scores'
  | 'pitchRegisters'
  | 'fach'
  | 'algoVersion'
  | 'capture'
>;

/**
 * `algoVersion`은 다른 공통 필드와 달리 `defined()`를 거치지 않는다 — 값이 있고 없고를
 * 가리지 않고 **항상** 싣는다(`score`·`mediaId`는 안 잰 시점이 있어 생략이 맞지만,
 * 알고리즘 버전은 제출 시점에 항상 정해져 있다).
 */
const commonOf = (input: AnalyTakeInput): Partial<AnalyCommonFields> => ({
  ...defined<AnalyCommonFields>({
    score: input.score,
    mediaId: input.mediaId,
    scores: input.session?.scores,
    pitchRegisters: input.session?.pitchRegisters,
    fach: input.session?.fach,
    capture: input.capture,
  }),
  algoVersion: algoVersionOf(input.source ?? 'native'),
});

/**
 * 노래 채점 구간 통계 → 서버 모양. 앱 `SongSegmentScore`와 서버 `SongPartScore`는 필드가
 * 같지만 **명시로 옮긴다** — 통째로 흘려보내면 앱에 필드가 느는 날 조용히 서버로 새어 나간다.
 */
const toSongPartScore = (
  part: SongSegmentScore | undefined,
): AnalySongPartScore | undefined =>
  part === undefined
    ? undefined
    : defined<AnalySongPartScore>({
        sungNotes: part.sungNotes,
        totalNotes: part.totalNotes,
        centDev: part.centDev,
        tendency: part.tendency,
      });

/**
 * 노래 채점 → 서버 모양.
 *
 * **`accuracy`는 눈금이 다르다.** 앱은 0~100으로 만들고(`songScoring.ts`의 `mean(...) * 100`)
 * 서버 계약은 0~1이다(SPEC §2.2 `SongScore.accuracy`). 같은 인터페이스의 `hitRatio`가 양쪽
 * 0~1로 일치하는 것이 서버 표기가 맞다는 근거다. 여기서 한 번만 나눈다 — 호출부가 각자
 * 나누면 한 곳이 빠지는 날 그 회차만 100배로 저장된다.
 */
const toSongScore = (
  score: SongScore | undefined,
): AnalySongScore | undefined =>
  score === undefined
    ? undefined
    : defined<AnalySongScore>({
        accuracy: score.accuracy / 100,
        hitRatio: score.hitRatio,
        sungNotes: score.sungNotes,
        totalNotes: score.totalNotes,
        low: toSongPartScore(score.low),
        high: toSongPartScore(score.high),
        timing:
          score.timing &&
          defined<AnalySongTiming>({
            meanOffsetMs: score.timing.meanOffsetMs,
            onTimeRatio: score.timing.onTimeRatio,
            matchedNotes: score.timing.matchedNotes,
          }),
      });

export const toAnalySubmitBody = (
  stereo: AnalyStereo,
  input: AnalyTakeInput,
): AnalySubmitBody => {
  const { metrics } = input;
  const common = commonOf(input);
  const range = metrics.range ?? undefined;

  switch (stereo) {
    case 'sustain':
      return {
        stereo,
        ...common,
        ...defined<AnalySustainMetrics>({
          sustainSec: metrics.sustainSec,
          hnrDb: metrics.hnrDb,
          resonanceRatio: metrics.resonanceRatio,
          resonanceLevelDb: metrics.resonanceLevelDb,
          resonanceCenterHz: metrics.resonanceCenterHz,
          brightnessHz: metrics.brightnessHz,
          bandwidthHz: metrics.bandwidthHz,
          vibrato: metrics.vibrato,
          breath: breathForServer(metrics.breath),
        }),
        ...defined<Pick<SustainSubmitBody, 'graph$'>>({ graph$: input.graph }),
      };
    case 'low':
      return {
        stereo,
        ...common,
        ...defined<AnalyLowMetrics>({
          lowNote: range?.lowNote ?? metrics.lowNote,
          lowHz: range?.lowHz ?? metrics.lowHz,
        }),
      };
    case 'high':
      return {
        stereo,
        ...common,
        ...defined<AnalyHighMetrics>({
          highNote: range?.highNote ?? metrics.highNote,
          highHz: range?.highHz ?? metrics.highHz,
        }),
      };
    case 'song':
      return {
        stereo,
        ...common,
        ...defined<AnalySongMetrics>({
          songScore: toSongScore(metrics.songScore),
        }),
      };
  }
};
